import { Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from '@/components/common/Layout';
import { Asistente } from '@/pages/asistente/Asistente';
import { Pedidos } from '@/pages/pedidos/Pedidos';
import { CopilotoHistorial } from '@/pages/copiloto-historial/CopilotoHistorial';
import { ProtectedRoute } from '@/components/common/ProtectedRoute';
import { ToastProvider } from '@/components/ui/Toast';
import { useAuthStore } from '@/store/auth.store';
import Login from '@/pages/login/Login';
import EntrarGlobal from '@/pages/entrar-global/EntrarGlobal';
import AcceptInvite from '@/pages/auth/AcceptInvite';
import ForgotPassword from '@/pages/auth/ForgotPassword';
import ResetPassword from '@/pages/auth/ResetPassword';
import TecnicoJobs from '@/pages/tecnico/TecnicoJobs';
import Dashboard from '@/pages/dashboard/Dashboard';
import Agents from '@/pages/agents/Agents';
import Channels from '@/pages/channels/Channels';
import Conversations from '@/pages/conversations/Conversations';
import ConversationDetail from '@/pages/conversations/ConversationDetail';
import Crm from '@/pages/crm/Crm';
import Contacts from '@/pages/contacts/Contacts';
import Campaigns from '@/pages/campaigns/Campaigns';
import { Descartes } from '@/pages/descartes/Descartes';
import { ModuloRoute } from '@/components/common/ModuloRoute';
import Settings from '@/pages/settings/Settings';
import { InstallationConfigPage } from '@/pages/installation/InstallationConfigPage';
import Users from '@/pages/users/Users';
import Employees from '@/pages/employees/Employees';
import Jobs from '@/pages/jobs/Jobs';
import Pendientes from '@/pages/pendientes/Pendientes';
import Despachos from '@/pages/despachos/Despachos';
import Tracking from '@/pages/tracking/Tracking';
import Eficiencia from '@/pages/eficiencia/Eficiencia';
import Publicidad from '@/pages/publicidad/Publicidad';
import Desempeno from '@/pages/desempeno/Desempeno';
import Clientes from '@/pages/clientes/Clientes';
import ClienteDetail from '@/pages/clientes/ClienteDetail';
import Sistema from '@/pages/sistema/Sistema';
import Entrenamiento from '@/pages/entrenamiento/Entrenamiento';
import Consumos from '@/pages/consumos/Consumos';
import Limites from '@/pages/limites/Limites';
import Simulador from '@/pages/simulador/Simulador';
import Tareas from '@/pages/tareas/Tareas';
import Zonas from '@/pages/zonas/Zonas';
import { esAmbientePruebas } from '@/lib/simulador-chat';

/** El panel corre en el ambiente de prueba (`vite --mode pruebas`). */
const AMBIENTE_PRUEBAS = esAmbientePruebas(import.meta.env.MODE);

/**
 * Shell admin con guardia de rol: un TECNICO no ve el CRM. Se le rebota a su
 * vista acotada (/mis-trabajos). El resto entra al <Layout> completo.
 */
function AdminShell() {
  const role = useAuthStore((s) => s.user?.role);
  if (role === 'TECNICO') return <Navigate to="/mis-trabajos" replace />;
  return <Layout />;
}

/** Router raíz de Conversia. Rutas privadas bajo <Layout> protegido. */
export default function App() {
  return (
    <>
      <Routes>
        <Route path="/login" element={<Login />} />
        {/* Entrar desde el Conversia global con un pase de un minuto (pages/entrar-global). */}
        <Route path="/entrar-global" element={<EntrarGlobal />} />
        {/* Ciclo de cuentas (páginas públicas): canje de invitación y reset. */}
        <Route path="/invite/:token" element={<AcceptInvite />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />

        {/* Vista del técnico: standalone (sin sidebar/CRM), solo requiere sesión. */}
        <Route
          path="/mis-trabajos"
          element={
            <ProtectedRoute>
              <TecnicoJobs />
            </ProtectedRoute>
          }
        />

        <Route
          element={
            <ProtectedRoute>
              <AdminShell />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<Dashboard />} />
          {/*
            La pantalla del asistente. NO es de admin: quien puede hablarle lo
            decide el directorio del copiloto, no el rol del panel.
          */}
          <Route path="/asistente" element={<Asistente />} />
          {/*
            El buzon de pedidos. NO es de admin: quien encuentra los problemas
            es quien usa el sistema todos los dias, y ponerle un rol delante es
            la forma mas rapida de no enterarse de nada.
          */}
          <Route path="/pedidos" element={<Pedidos />} />
          {/*
            El historial completo del copiloto. Sin ProtectedRoute de rol: quien
            lo ve no lo decide el rol del panel sino la API (OWNER o dueño del
            directorio). La pantalla pregunta el permiso y, sin él, lo dice.
          */}
          <Route path="/historial-copiloto" element={<CopilotoHistorial />} />
          <Route path="/conversations" element={<Conversations />} />
          <Route path="/conversations/:id" element={<ConversationDetail />} />
          <Route path="/crm" element={<Crm />} />
          <Route path="/agents" element={<Agents />} />
          <Route path="/channels" element={<Channels />} />
          <Route path="/contacts" element={<Contacts />} />
          <Route
            path="/campaigns"
            element={
              <ModuloRoute modulo="campanas">
                <Campaigns />
              </ModuloRoute>
            }
          />
          <Route
            path="/descartes"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <ModuloRoute modulo="descartes">
                  <Descartes />
                </ModuloRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/installation"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <InstallationConfigPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tareas"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Tareas />
              </ProtectedRoute>
            }
          />
          <Route
            path="/zonas"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Zonas />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employees"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <ModuloRoute modulo="instalaciones">
                  <Employees />
                </ModuloRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/jobs"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <ModuloRoute modulo="instalaciones">
                  <Jobs />
                </ModuloRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pendientes"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Pendientes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/despachos"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <ModuloRoute modulo="instalaciones">
                  <Despachos />
                </ModuloRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/clientes"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Clientes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clientes/:id"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <ClienteDetail />
              </ProtectedRoute>
            }
          />
          {/* Eficiencia — portada de Tony Boga el 4-nov-2026. Sin `ModuloRoute`
              a proposito: no cuelga de ningun modulo opcional, y son numeros
              de plata y de desempeno, asi que ADMIN y nadie mas. */}
          <Route
            path="/eficiencia"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Eficiencia />
              </ProtectedRoute>
            }
          />
          <Route
            path="/publicidad"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Publicidad />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tracking"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <ModuloRoute modulo="instalaciones">
                  <Tracking />
                </ModuloRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/desempeno"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <ModuloRoute modulo="instalaciones">
                  <Desempeno />
                </ModuloRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Users />
              </ProtectedRoute>
            }
          />
          {/* Simulador: la ruta ni siquiera existe fuera del ambiente de prueba. */}
          {AMBIENTE_PRUEBAS && (
            <Route
              path="/simulador"
              element={
                <ProtectedRoute requireRole="ADMIN">
                  <Simulador />
                </ProtectedRoute>
              }
            />
          )}
          <Route
            path="/sistema"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Sistema />
              </ProtectedRoute>
            }
          />
          <Route
            path="/entrenamiento"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Entrenamiento />
              </ProtectedRoute>
            }
          />
          {/* Reservado del OWNER (F2): consumo USD y límites de gasto. La API
              ya responde 403 a un ADMIN; esto solo evita mostrarle una página
              rota. */}
          <Route
            path="/consumos"
            element={
              <ProtectedRoute requireRole="OWNER">
                <Consumos />
              </ProtectedRoute>
            }
          />
          <Route
            path="/limites"
            element={
              <ProtectedRoute requireRole="OWNER">
                <Limites />
              </ProtectedRoute>
            }
          />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <ToastProvider />
    </>
  );
}
